import React, { useCallback } from "react";
import { Tour, components as tourComponents } from "@reactour/tour";
import "@reactour/tour/dist/index.css";
import { Box, Typography, Button } from "@mui/material";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const Tutorial = ({
  title,
  steps,
  isOpen,
  setIsOpen,
  currentStep,
  setCurrentStep,
  ...other
}) => {
  const resetTour = useCallback(
    ({ setIsOpen: close, setCurrentStep: setStep } = {}) => {
      if (setStep) setStep(0);
      if (close) close(false);
      if (setCurrentStep) setCurrentStep(0);
      if (setIsOpen) setIsOpen(false);
    },
    [setIsOpen, setCurrentStep],
  );

  const NavigationWithSkip = ({
    currentStep,
    setIsOpen: close,
    setCurrentStep: setStep,
    steps,
    ...props
  }) => {
    const { Navigation: DefaultNavigation } = tourComponents;
    return (
      <Box>
        <DefaultNavigation
          currentStep={currentStep}
          setIsOpen={close}
          setCurrentStep={setStep}
          steps={steps}
          {...props}
        />
        {currentStep < steps.length - 1 && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Button size="small" onClick={() => resetTour({ setIsOpen: close, setCurrentStep: setStep })}>
              Skip
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const createContent = (step) => (
    <Box sx={{ borderRadius: 2, px: 2, textAlign: "left" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", flexGrow: 1 }}>
          {step.final ? "Ready to Explore?" : step.title || title}
        </Typography>
      </Box>
      <Typography sx={{ mb: -1}}>{step.content}</Typography>
    </Box>
  );

  const mappedSteps = steps.map((step) => ({
    selector: step.selector?.current || step.selector,
    position: step.position,
    content: () => createContent(step),
  }));


  return (
    <Tour
      steps={mappedSteps}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onClickMask={resetTour}
      onClickClose={resetTour}
      showCloseButton={false}
      scrollSmooth
      prevButton={({ Button: Btn, currentStep, ...props }) => (
        <Btn
          {...props}
          kind="prev"
          hideArrow
          style={{ visibility: currentStep === 0 ? "hidden" : "visible" }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <NavigateBeforeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
            Previous
          </Box>
        </Btn>
      )}
      nextButton={({ Button: Btn, stepsLength, currentStep, ...props }) => {
        const isLast = currentStep === stepsLength - 1;
        const handleClick = isLast ? () => resetTour() : props.onClick;
        return (
          <Btn {...props} kind="next" hideArrow onClick={handleClick}>
            <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              {isLast ? "Finish" : "Next"}
              {isLast ? (
                <CheckRoundedIcon fontSize="small" sx={{ ml: 0.5 }} />
              ) : (
                <NavigateNextRoundedIcon fontSize="small" sx={{ ml: 0.5 }} />
              )}
            </Box>
          </Btn>
        );
      }}
      components={{ Navigation: NavigationWithSkip }}
      styles={{
        popover: (base) => ({
          ...base,
          borderRadius: 8,
          padding: 16,
          width: "auto",
          maxWidth: 450,
        }),
        controls: (base) => ({
          ...base,
          justifyContent: "center",
          gap: 24,
        }),
        navigation: (base) => ({
          ...base,
          justifyContent: "center",
        }),
      }}
      {...other}
    />
  );
};

export default Tutorial;
